import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MdDeleteOutline, MdModeEditOutline } from 'react-icons/md';
import { userMapsApi } from '../../services/userMapsApi';
import { areaShapeLoader } from '../../geo/areaShapes';
import NavBar from '../../components/common/NavBar';
import AreaPicker, { AreaIdBadges } from '../../components/areas/AreaPicker';
import type { RowExtras, RowState } from '../../components/areas/AreaPicker';
import { projectsApi, Project } from '../../services/projectsApi';
import DataStateNotice from '../../components/data/DataStateNotice';
import { COUNTRIES, areaCountry } from '../../config/countries';
import { correctAreaType } from '../../utils/areaLookup';
import { describeRequestError } from '../../services/http';
import { areaEditPath } from '../../utils/areaEditPath';
import DraftDataBadge from '../../components/data/DraftDataBadge';
import CountryFilter from '../../components/areas/CountryFilter';
import { canUse } from '../../config/catalog';
import { useCountryFilter } from '../../hooks/useCountryFilter';

interface SavedArea {
  id: string;
  name: string;
  description: string;
  type: string;
  ids?: string[];
  geometry?: string;
  country_code?: string | null;
}

interface ActionError {
  title: string;
  body: string;
}

const ActivitiesSelectPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // The country filter: All by default, or the country of the ?country= in the URL.
  const { country: filter, setCountry: setFilter } = useCountryFilter();
  const [savedAreas, setSavedAreas] = useState<SavedArea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [areaToDelete, setAreaToDelete] = useState<SavedArea | null>(null);
  const [loadingEdit, setLoadingEdit] = useState<string | null>(null);
  const [loadingArea, setLoadingArea] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [actionError, setActionError] = useState<ActionError | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const refreshProjects = useCallback(async () => {
    try {
      setProjects(await projectsApi.list());
    } catch (err) {
      console.warn('Could not load projects:', err);
    }
  }, []);

  useEffect(() => {
    refreshProjects();
  }, [refreshProjects]);

  // Saved areas keyed by id, to resolve a project's members.
  const areaById = useMemo(
    () => Object.fromEntries(savedAreas.map((a) => [a.id, a])) as Record<string, SavedArea>,
    [savedAreas]
  );

  // Every saved area is listed, grouped by country and type; the filter narrows the list to one country.
  const listed = useMemo(
    () => (filter ? savedAreas.filter((a) => areaCountry(a) === filter) : savedAreas),
    [savedAreas, filter],
  );
  // The areas of the list Activities can use at all; none of them means the filter shows nothing useful.
  const usableListed = useMemo(() => listed.filter((a) => canUse('activities', a, []).supported), [listed]);

  // A project shows the members the filter lets through; a project with none is hidden.
  const visibleProjects = useMemo(
    () =>
      projects
        .map((p) => ({
          project: p,
          members: p.areaIds
            .map((id) => areaById[id])
            .filter((a): a is SavedArea => Boolean(a) && (!filter || areaCountry(a) === filter)),
        }))
        .filter((entry) => entry.members.length > 0),
    [projects, areaById, filter],
  );

  useEffect(() => {
    const loadSavedAreas = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await userMapsApi.loadGeometries();
        const sortedAreas = [...response.maps].sort((a, b) => a.name.localeCompare(b.name));
        setSavedAreas(sortedAreas);
      } catch (err) {
        console.error('Failed to load saved areas:', err);
        setError(describeRequestError(err));
      } finally {
        setLoading(false);
      }
    };

    loadSavedAreas();
  }, []);

  // The shapes of a saved area's ids, from the maps API. Needs the area's country.
  const loadGeos = (area: SavedArea, type: string, ids: string[]) =>
    areaShapeLoader.load({ id: area.id, type, ids, country_code: area.country_code });

  const handleLoadArea = async (area: SavedArea) => {
    try {
      setActionError(null);
      setLoadingArea(area.id);
      const correctedType = correctAreaType(area);

      if (area.ids) {
        const geoData = await loadGeos(area, correctedType, area.ids);
        navigate('/activities-map', {
          state: {
            savedArea: { ...area, type: correctedType },
            geometries: geoData,
            pickerSearch: location.search,
          },
        });
      } else if (area.geometry) {
        navigate('/activities-map', {
          state: { savedArea: area, wktGeometry: area.geometry, pickerSearch: location.search },
        });
      } else {
        throw new Error('No geometry data available for this area');
      }
    } catch (error) {
      console.error('Failed to load geometries:', error);
      setActionError({ title: "Couldn't open this area", body: describeRequestError(error) });
    } finally {
      setLoadingArea(null);
    }
  };

  const handleEdit = async (area: SavedArea) => {
    try {
      setActionError(null);
      const path = areaEditPath(area.type);

      if (path === '/custom-polygon') {
        if (!area.geometry) throw new Error('No geometry data available for editing this area');
        navigate(path, {
          state: { editMode: true, savedArea: area, wktGeometry: area.geometry },
        });
      } else if (area.ids) {
        setLoadingEdit(area.id);
        const correctedType = correctAreaType(area);
        const geoData = await loadGeos(area, correctedType, area.ids);
        navigate(path, {
          state: {
            editMode: true,
            savedArea: { ...area, type: correctedType },
            geometries: geoData,
          },
        });
      } else {
        throw new Error(
          path === '/road-links'
            ? 'No road IDs available for editing this area'
            : 'No geometry data available for editing this area',
        );
      }
    } catch (error) {
      console.error('Failed to edit area:', error);
      setActionError({ title: "Couldn't edit this area", body: describeRequestError(error) });
    } finally {
      setLoadingEdit(null);
    }
  };

  const handleDelete = (area: SavedArea) => {
    setAreaToDelete(area);
    setDeleteError(null);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!areaToDelete) return;
    try {
      await userMapsApi.deleteGeometries(areaToDelete.id);
      setSavedAreas((prev) => prev.filter((a) => a.id !== areaToDelete.id));
      setIsDeleteModalOpen(false);
      setAreaToDelete(null);
    } catch (error) {
      console.error('Failed to delete area:', error);
      setDeleteError(describeRequestError(error));
    }
  };

  const renderRowExtras = (area: SavedArea, state: RowState): RowExtras => ({
    badge: state.supported ? (
      <DraftDataBadge country={areaCountry(area)} product="activities" geoType={correctAreaType(area)} />
    ) : null,
    actions: (
      <>
        <button
          className={`btn btn-ghost btn-sm btn-circle text-primary hover:bg-primary hover:text-primary-content ${loadingEdit === area.id ? 'btn-disabled' : ''}`}
          onClick={() => handleEdit(area)}
          title="Edit area"
          disabled={loadingEdit === area.id}
        >
          {loadingEdit === area.id ? (
            <span className="loading loading-spinner loading-sm"></span>
          ) : (
            React.createElement(MdModeEditOutline as React.ComponentType<{ size?: number }>, { size: 18 })
          )}
        </button>
        <button
          className="btn btn-ghost btn-sm btn-circle text-error hover:bg-error hover:text-error-content"
          onClick={() => handleDelete(area)}
          title="Delete area"
        >
          {React.createElement(MdDeleteOutline as React.ComponentType<{ size?: number }>, { size: 18 })}
        </button>
      </>
    ),
    body: <AreaIdBadges area={area} muted={!state.supported} />,
    footer: (
      <div className="card-actions justify-end">
        <button
          className={`btn btn-primary btn-sm ${loadingArea === area.id ? 'btn-disabled' : ''}`}
          onClick={() => handleLoadArea(area)}
          disabled={loadingArea === area.id}
        >
          {loadingArea === area.id ? (
            <>
              <span className="loading loading-spinner loading-xs"></span>
              Loading...
            </>
          ) : (
            'Analyze Activities'
          )}
        </button>
      </div>
    ),
  });

  return (
    <div className="min-h-screen bg-base-200 flex flex-col">
      <NavBar title="Activities - Select Area" showBackButton backTo="/" />

      <div className="flex flex-col items-center justify-center flex-grow p-4">
        <div className="w-full max-w-6xl">
          <div className="text-center mb-8">
            <h1 className="text-5xl font-bold">Select an Area</h1>
            <p className="text-lg text-base-content/70 mt-2">
              Choose a saved area to analyze activities
            </p>
            <CountryFilter areas={savedAreas} value={filter} onChange={setFilter} className="mt-4" />
          </div>

          {actionError ? (
            <div className="max-w-2xl mx-auto mb-6">
              <DataStateNotice
                tone="error"
                title={actionError.title}
                body={actionError.body}
                action={{ label: 'Dismiss', onClick: () => setActionError(null) }}
              />
            </div>
          ) : null}

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="loading loading-spinner loading-lg"></div>
              <span className="ml-4 text-lg">Loading saved areas...</span>
            </div>
          ) : error ? (
            <div className="alert alert-error max-w-2xl mx-auto">
              <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error}</span>
            </div>
          ) : savedAreas.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-lg text-base-content/70">No saved areas yet</p>
              <p className="text-sm text-base-content/50 mt-2">Create and save an area to see it here.</p>
              <div className="mt-6">
                <button className="btn btn-primary" onClick={() => navigate('/?section=areas')}>
                  Create New Area
                </button>
              </div>
            </div>
          ) : filter && usableListed.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-lg text-base-content/70">{`No saved areas you can use for Activities in ${COUNTRIES[filter].label}`}</p>
              <div className="mt-6">
                <button className="btn btn-primary" onClick={() => setFilter(null)}>
                  Show all countries
                </button>
              </div>
            </div>
          ) : (
            <>
              <AreaPicker
                product="activities"
                country={filter}
                mode="single"
                selection={[]}
                projects={visibleProjects.map(({ project, members }) => ({ id: project.id, name: project.name, members }))}
                groups={[{ id: 'areas', areas: listed }]}
                renderRowExtras={renderRowExtras}
              />
              <div className="text-center mt-8">
                <button className="btn btn-outline" onClick={() => navigate('/?section=areas')}>
                  Create New Area
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {isDeleteModalOpen && (
        <div className="modal modal-open">
          <div className="modal-box max-w-md">
            <h3 className="text-lg font-bold mb-4">Confirm Delete</h3>
            <p className="text-base-content/80 mb-6">
              Are you sure you want to delete "{areaToDelete?.name}"? This action cannot be undone.
            </p>
            {deleteError ? (
              <div className="mb-6">
                <DataStateNotice tone="error" title="Couldn't delete this area" body={deleteError} />
              </div>
            ) : null}
            <div className="modal-action">
              <button className="btn btn-ghost" onClick={() => { setIsDeleteModalOpen(false); setAreaToDelete(null); }}>
                Cancel
              </button>
              <button className="btn btn-error" onClick={handleConfirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivitiesSelectPage;
